import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Typography,
  Card,
  Spin,
  Alert,
  Modal,
  Button,
  Select,
  Tooltip,
} from 'antd';
import type { SelectProps } from 'antd';
// Hooks
import { useBrandDetails } from 'hooks';
// Helpers
import { formatPrice } from '../ManageMerch/helpers';
// UI
import { successNotification, errorNotification } from 'ui/Notification';
import CbAccountBalancePanel from '../CbAccountBalancePanel/CbAccountBalancePanel';
import EntitlementsSection from './EntitlementsSection/EntitlementsSection';
// Styles
import styles from './BrandDetailsComponent.module.scss';

const { Title, Text } = Typography;

type PlanOption = SelectProps['options'];

const BrandDetails: React.FC = () => {
  const { ownerId } = useParams<{ ownerId: string }>();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isVoidCancelModalOpen, setIsVoidCancelModalOpen] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [downgradeLoading, setDowngradeLoading] = useState(false);

  const {
    subscriptionQuery,
    plansQuery,
    agencyQuery,
    cancelDowngrade,
    voidCancellation,
    updateSubscription,
    cancelSubscription,
    setEntitlement,
  } = useBrandDetails({ ownerId });

  const { data, loading, error, refetch } = subscriptionQuery;
  const { data: plansData } = plansQuery;
  const stripeCustomerUrl = agencyQuery.data?.getAgency?.stripeCustomerUrl;

  // Initialize current plan when data loads
  useEffect(() => {
    const priceId = data?.getMyCbSubscription?.items?.[0]?.price?.id;
    if (priceId && (!selectedPlanId || selectedPlanId === '')) {
      setSelectedPlanId(priceId);
    }
  }, [data, selectedPlanId]);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={`Failed to load brand details: ${error.message}`}
        type="error"
        showIcon
      />
    );
  }

  if (!data?.getMyCbSubscription) {
    return (
      <Alert
        message="No Data"
        description="No brand subscription data found for this agency."
        type="warning"
        showIcon
      />
    );
  }

  const {
    id,
    status,
    items,
    startDate,
    cancelAt,
    cancelAtPeriodEnd,
    currentPeriodStart,
    currentPeriodEnd,
    downgradeStartAt,
    downgradeScheduleMeta,
    lastPaymentFailedAt,
    defaultBillingAddressId,
    defaultPaymentMethod,
  } = data.getMyCbSubscription;

  const planName = items?.[0]?.price?.product?.name || '';
  const currentPriceAmount = items?.[0]?.price?.unitAmount || 0;
  const currentPriceId = items?.[0]?.price?.id || '';
  const cost = formatPrice(currentPriceAmount);
  const currentPeriodEndDate = dayjs(currentPeriodEnd).format('MM/DD/YYYY');
  const currentPeriodStartDate = dayjs(currentPeriodStart).format('MM/DD/YYYY');
  const startDateFormatted = dayjs(startDate).format('MM/DD/YYYY');

  // all plans except free plan have additional information to display
  const isPaidPlan = id !== 'free';

  // Downgrade information
  const isDowngraded = !!downgradeStartAt && !!downgradeScheduleMeta;
  const downgradeDate = downgradeStartAt
    ? dayjs(downgradeStartAt).format('MM/DD/YYYY')
    : null;

  // Cancellation information
  const isCancellationScheduled = !!cancelAtPeriodEnd && !!cancelAt;
  const cancellationDate = cancelAt
    ? dayjs(cancelAt).format('MM/DD/YYYY')
    : null;

  // Payment failure information
  const hasPaymentFailed = !!lastPaymentFailedAt;
  const paymentFailedDate = lastPaymentFailedAt
    ? dayjs(lastPaymentFailedAt).format('MM/DD/YYYY')
    : null;

  const currentMonthlyPrice = items?.[0]?.price?.monthlyEquivalentPrice || 0;
  const currentSubscriptionInterval =
    items?.[0]?.price?.recurringInterval || 'monthly';

  // Prepare plan options for select with disabled state for upgrades
  // Filter prices to match current subscription interval (monthly or yearly)
  const planOptions: PlanOption = [];
  const planMonthlyPrices: { [key: string]: number } = {};

  if (plansData?.getCbPlans) {
    plansData.getCbPlans.forEach((plan) => {
      // Find price matching current subscription interval (monthly or yearly)
      let selectedPrice = plan.prices?.find(
        (p) => p.recurringInterval === currentSubscriptionInterval
      );
      // Fallback to monthly if no matching interval found
      if (!selectedPrice) {
        selectedPrice = plan.prices?.find(
          (p) => p.recurringInterval === 'monthly'
        );
      }
      // Fallback to first available price
      if (!selectedPrice && plan.prices && plan.prices.length > 0) {
        selectedPrice = plan.prices[0];
      }

      if (selectedPrice) {
        const planValue = selectedPrice.id;
        const isUpgrade =
          selectedPrice.monthlyEquivalentPrice > currentMonthlyPrice;
        const isCurrentPlan = planValue === currentPriceId;

        planMonthlyPrices[planValue] = selectedPrice.monthlyEquivalentPrice;

        // Always display monthlyEquivalentPrice for easier comparison
        const displayPrice = selectedPrice.monthlyEquivalentPrice;

        planOptions.push({
          label: `${plan.name} - ${formatPrice(displayPrice)}${
            isCurrentPlan ? ' (Current)' : ''
          }`,
          value: planValue,
          disabled: isUpgrade,
        });
      }
    });
  }

  // Check if selected plan is a valid downgrade
  const isSelectedPlanDowngrade =
    selectedPlanId && planMonthlyPrices[selectedPlanId] !== undefined
      ? planMonthlyPrices[selectedPlanId] < currentMonthlyPrice
      : false;

  // Check if selected plan exists in available plans
  const isSelectedPlanAvailable = selectedPlanId
    ? planMonthlyPrices[selectedPlanId] !== undefined
    : false;

  const isSelectedPlanFree =
    selectedPlanId && planMonthlyPrices[selectedPlanId] !== undefined
      ? planMonthlyPrices[selectedPlanId] === 0
      : false;

  const shouldShowChangeButton =
    selectedPlanId && selectedPlanId !== currentPriceId;

  // Find the proper plan option to display the label in the select input
  const currentPlanName = planName.toUpperCase();
  let matchingOption = planOptions.find((opt) => {
    if (!opt.label || typeof opt.label !== 'string') return false;
    const optionPlanName = opt.label
      .split(' - ')[0]
      .replace(' (Current)', '')
      .toUpperCase();
    return optionPlanName === currentPlanName;
  });

  if (!matchingOption && currentPriceId) {
    matchingOption = planOptions.find((opt) => opt.value === currentPriceId);
  }

  if (selectedPlanId && selectedPlanId.trim() !== '') {
    const selectedOption = planOptions.find(
      (opt) => opt.value === selectedPlanId
    );
    if (selectedOption) {
      matchingOption = selectedOption;
    }
  }

  // Use the matching option's value, or fallback to selectedPlanId/currentPriceId
  const displayValue = matchingOption
    ? matchingOption.value
    : selectedPlanId || currentPriceId || undefined;

  const handleDowngrade = async () => {
    if (!selectedPlanId) {
      errorNotification('No plan selected');
      return;
    }
    if (!isSelectedPlanAvailable) {
      errorNotification('Selected plan is not available');
      return;
    }
    if (!isSelectedPlanDowngrade) {
      errorNotification('Selected plan must be a downgrade from current plan');
      return;
    }
    setDowngradeLoading(true);
    try {
      if (isSelectedPlanFree) {
        // Use cancelCbSubscription for FREE plan
        await cancelSubscription({
          input: {
            ownerId: ownerId || '',
          },
        });
        successNotification('Subscription cancelled successfully!');
      } else {
        // Require payment method and billing address for the API to work with paid plans (fallback, but it should not be able to happen)
        if (!defaultPaymentMethod?.id || !defaultBillingAddressId) {
          errorNotification(
            'Cannot downgrade due to missing payment method or billing address'
          );
          setDowngradeLoading(false);
          return;
        }
        // Use updateMyCbSubscription for paid downgrades (API requires payment method and billing address)
        const input: any = {
          ownerId: ownerId || '',
          items: [{ priceId: selectedPlanId, quantity: 1 }],
          addressId: defaultBillingAddressId,
          paymentMethodId: defaultPaymentMethod.id,
        };
        await updateSubscription({
          input,
        });
        successNotification('Subscription downgrade scheduled successfully!');
      }
      setSelectedPlanId('');
      refetch();
    } catch (err: any) {
      console.error('Downgrade error:', err);
      errorNotification(
        err?.message ||
          'Failed to process subscription change, please try again later'
      );
    } finally {
      setDowngradeLoading(false);
    }
  };

  const handleCancelDowngrade = async () => {
    try {
      await cancelDowngrade({
        input: {
          ownerId: ownerId || '',
        },
      });
      setIsCancelModalOpen(false);
      successNotification('Subscription downgrade cancelled successfully!');
      refetch();
    } catch (err: any) {
      errorNotification(
        err?.message || 'Something went wrong, please try again later'
      );
    }
  };

  const handleVoidCancellation = async () => {
    try {
      await voidCancellation({
        input: {
          ownerId: ownerId || '',
        },
      });
      setIsVoidCancelModalOpen(false);
      successNotification('Subscription cancellation voided successfully!');
      refetch();
    } catch (err: any) {
      errorNotification(
        err?.message || 'Something went wrong, please try again later'
      );
    }
  };

  return (
    <div className={styles.root}>
      <Title level={2}>Brand Details</Title>
      <Card>
        {stripeCustomerUrl && (
          <div className={styles.stripeCustomerLink}>
            <Tooltip title="Opens this brand's customer profile in the Stripe dashboard, in a new tab">
              <a
                href={stripeCustomerUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Stripe Customer
              </a>
            </Tooltip>
          </div>
        )}
        <Title level={4}>Subscription Information</Title>
        <div className={styles.card}>
          <div>
            <Text strong>Subscription ID: </Text>
            <Text>{id}</Text>
          </div>
          <div>
            <Text strong>Plan Name: </Text>
            <Text>{planName}</Text>
          </div>
          <div>
            <Text strong>Status: </Text>
            <Text>{status}</Text>
          </div>
          {isPaidPlan && (
            <>
              <div>
                <Text strong>Cost: </Text>
                <Text>{cost}</Text>
              </div>
              <div>
                <Text strong>Billing Period: </Text>
                <Text>
                  {currentSubscriptionInterval
                    ? currentSubscriptionInterval.charAt(0).toUpperCase() +
                      currentSubscriptionInterval.slice(1)
                    : 'N/A'}
                </Text>
              </div>
              <div>
                <Text strong>Current Period Start: </Text>
                <Text>{currentPeriodStartDate}</Text>
              </div>
              <div>
                <Text strong>Next Billing Date: </Text>
                <Text>{currentPeriodEndDate}</Text>
              </div>
              <div>
                <Text strong>Start Date: </Text>
                <Text>{startDateFormatted}</Text>
              </div>
            </>
          )}

          {isDowngraded && downgradeDate && (
            <div className={styles.downgradeWrapper}>
              <div className={styles.notice}>
                <span className={styles.noticeLabel}>
                  Plan Downgrade Scheduled
                </span>
              </div>
              <div className={styles.downgradeDetails}>
                <div>
                  <div>
                    <Text strong>Downgrade Effective Date: </Text>
                    <Text>{downgradeDate}</Text>
                  </div>
                  {downgradeScheduleMeta?.items &&
                    downgradeScheduleMeta.items.length > 0 && (
                      <div>
                        <div>
                          <Text strong>Downgrade Details: </Text>
                          <Text>
                            {downgradeScheduleMeta.items.map((item, index) => (
                              <span key={index}>
                                {item.price?.product?.name}
                                {index < downgradeScheduleMeta.items.length - 1
                                  ? ', '
                                  : ''}
                              </span>
                            ))}
                          </Text>
                        </div>
                      </div>
                    )}
                </div>
                <Button
                  className={styles.cancelDowngradeButton}
                  onClick={() => setIsCancelModalOpen(true)}
                >
                  Cancel Downgrade
                </Button>
              </div>
            </div>
          )}

          {isCancellationScheduled && cancellationDate && (
            <>
              <div className={styles.notice}>
                <span className={styles.noticeLabel}>
                  Subscription Cancellation Scheduled
                </span>
              </div>
              <div>
                <Text strong>Cancellation Date: </Text>
                <Text>{cancellationDate}</Text>
              </div>
              <div>
                <Text strong>Cancel at Period End: </Text>
                <Text>{cancelAtPeriodEnd ? 'Yes' : 'No'}</Text>
              </div>
              <div className={styles.actionsSection}>
                <Button onClick={() => setIsVoidCancelModalOpen(true)}>
                  Void Cancellation
                </Button>
              </div>
            </>
          )}

          {hasPaymentFailed && paymentFailedDate && (
            <>
              <div className={styles.notice}>
                <span className={styles.noticeLabel}>Payment Failed</span>
              </div>
              <div>
                <Text strong>Last Payment Failed: </Text>
                <Text>{paymentFailedDate}</Text>
              </div>
            </>
          )}

          {isPaidPlan && planOptions.length > 0 && (
            <div className={styles.planChangeSection}>
              <Title level={5}>Change Plan</Title>
              <div className={styles.planChangeControls}>
                <Select
                  className={styles.selectInput}
                  placeholder="Select a plan to downgrade to"
                  value={displayValue}
                  onChange={(value) => setSelectedPlanId(String(value))}
                  options={planOptions}
                />
                {shouldShowChangeButton && (
                  <>
                    <Button
                      type="primary"
                      onClick={() => {
                        handleDowngrade();
                      }}
                      loading={downgradeLoading}
                    >
                      {isSelectedPlanFree
                        ? 'Cancel Subscription'
                        : 'Schedule Downgrade'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}

          {ownerId && (
            <EntitlementsSection
              ownerId={ownerId}
              setEntitlement={setEntitlement}
            />
          )}
        </div>
      </Card>

      {ownerId && (
        <div style={{ marginTop: 24 }}>
          <CbAccountBalancePanel ownerId={ownerId} />
        </div>
      )}

      <Modal
        visible={isCancelModalOpen}
        onCancel={() => setIsCancelModalOpen(false)}
        onOk={handleCancelDowngrade}
        title="Cancel Subscription Downgrade"
        okText="Cancel Downgrade"
        cancelText="Keep Downgrade"
      >
        <p>
          Are you sure you want to cancel the scheduled subscription downgrade?
          If you do, current plan would remain active.
        </p>
      </Modal>

      <Modal
        visible={isVoidCancelModalOpen}
        onCancel={() => setIsVoidCancelModalOpen(false)}
        onOk={handleVoidCancellation}
        title="Void Subscription Cancellation"
        okText="Void Cancellation"
        cancelText="Keep Cancellation"
      >
        <p>
          Are you sure you want to void the scheduled subscription cancellation?
          If you do, subscription would continue as normal.
        </p>
      </Modal>
    </div>
  );
};

export default BrandDetails;
